import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useUser } from '../UserContext';

function formatAccruedAt(ts) {
  if (!ts) return '—';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : null;
  if (!date) return '—';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatLevel(level) {
  const raw = String(level || '').toLowerCase();
  if (raw === 'accredited' || raw === 'global') return 'Global';
  if (raw === 'certified') return 'Certified';
  if (raw === 'foundation') return 'Foundation';
  return level || '—';
}

function formatCategory(category) {
  if (category === 'accreditor_fee') return 'Accreditor fee';
  if (category === 'admin_fee') return 'Admin fee';
  return String(category || '—').replace(/_/g, ' ');
}

const StaffEarnings = () => {
  const { user, isApproved } = useUser();
  const [earnings, setEarnings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid || !isApproved) return undefined;
    const q = query(
      collection(db, 'financeEarnings'),
      where('payeeUid', '==', user.uid),
      orderBy('accruedAt', 'desc'),
      limit(100)
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setEarnings(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error('financeEarnings listener failed:', err);
        setError(err?.message || 'Could not load earnings.');
        setIsLoading(false);
      }
    );
    return () => unsub();
  }, [user?.uid, isApproved]);

  const summary = useMemo(() => {
    const totalGbp = earnings.reduce((sum, row) => {
      if (row.status === 'pending_rates') return sum;
      const n = Number(row.amountGbp);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
    const pending = earnings.filter((row) => row.status === 'pending_rates').length;
    return { totalGbp, pending };
  }, [earnings]);

  if (!isApproved) return null;
  if (isLoading) return <div className="admin-placeholder">Loading your earnings…</div>;

  return (
    <div className="audit-trail-view admin-finance-view">
      <div className="registry-controls" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <div className="stat-pill">{earnings.length} earning row(s)</div>
        <div className="stat-pill">
          Accrued total: £{summary.totalGbp.toLocaleString('en-GB')}
        </div>
        {summary.pending > 0 && (
          <div className="stat-pill" style={{ borderColor: '#fbbf24' }}>
            {summary.pending} pending rate(s)
          </div>
        )}
      </div>

      <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 16px', maxWidth: '720px' }}>
        Fees accrued when accreditation cycles are finalised and archived. Amounts use your pay rate
        card at finalise time; rows marked pending need rates set in Staff Management.
      </p>

      {error && (
        <div className="admin-banner-warning" style={{ marginBottom: '16px' }}>
          {error}
          {error.includes('index') && (
            <span> — deploy Firestore indexes if this persists after first query.</span>
          )}
        </div>
      )}

      <div className="log-table">
        <div className="log-header" style={{ gridTemplateColumns: '100px 1.4fr 1fr 90px 110px 90px 1fr' }}>
          <div>Accrued</div>
          <div>School</div>
          <div>Category</div>
          <div>Level</div>
          <div>GBP</div>
          <div>Status</div>
          <div>Archive</div>
        </div>

        {!earnings.length && (
          <div className="admin-placeholder" style={{ marginTop: '24px' }}>
            No earnings yet — rows appear when assigned accreditations are finalised.
          </div>
        )}

        {earnings.map((row) => (
          <div
            key={row.id}
            className="log-row"
            style={{ gridTemplateColumns: '100px 1.4fr 1fr 90px 110px 90px 1fr', alignItems: 'start' }}
          >
            <div>{formatAccruedAt(row.accruedAt)}</div>
            <div>
              <strong>{row.schoolName || '—'}</strong>
              {row.schoolId && (
                <div className="id-tag" style={{ marginTop: '4px' }}>
                  {row.schoolId}
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.82rem' }}>{formatCategory(row.category)}</div>
            <div>{formatLevel(row.level)}</div>
            <div>
              {row.status === 'pending_rates'
                ? '—'
                : row.amountGbp != null && Number.isFinite(Number(row.amountGbp))
                  ? `£${Number(row.amountGbp).toLocaleString('en-GB')}`
                  : '—'}
            </div>
            <div style={{ fontSize: '0.78rem' }}>
              <span className={`path-tag ${row.status === 'pending_rates' ? 'path-tag--warn' : ''}`}>
                {row.status === 'pending_rates' ? 'pending rates' : row.status || 'accrued'}
              </span>
            </div>
            <div className="id-tag">{row.archiveId || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffEarnings;
